<template>
  <b-sidebar
    id="detail-overtime-drawer"
    :visible="visible"
    title="Detail Lembur"
    backdrop-variant="dark"
    backdrop
    right
    shadow
    width="500px"
    sidebar-class="right-drawer"
    aria-hidden="false"
    @hidden="onClose"
  >
    <BOverlay
      :show="isLoading"
      spinner-variant="primary"
      variant="light"
      opacity=".6"
      rounded="sm"
    >
      <div class="p-[1rem] space-y-[1rem]">
        <!-- Employee Profile Card -->
        <div class="border rounded-2xl p-[.875rem] bg-white flex items-center justify-between shadow">
          <div class="flex items-center gap-[.75rem]">
            <img
              :src="profilePhoto"
              alt="User Profile"
              class="rounded-lg size-10 object-cover"
            >
            <div>
              <div class="text-sm font-semibold text-black">
                {{ employeeName }}
              </div>
              <div class="text-xs text-[#828282]">
                {{ employeeRole }}{{ employeeDivision ? ` • ${employeeDivision}` : '' }}
              </div>
            </div>
          </div>
          <BBadge
            :variant="statusBadgeVariant"
            class="text-xs px-[.5rem] py-[.75rem] rounded-full !rounded-xl"
          >
            {{ statusLabel }}
          </BBadge>
        </div>

        <!-- Custom Dashed Separator -->
        <div class="dashed-divider my-[1rem]" />

        <!-- Detail Pengajuan Lembur Card -->
        <div class="border rounded-2xl p-[1rem] bg-white space-y-[1rem] shadow">
          <div>
            <div class="text-sm font-semibold text-black mb-[.25rem]">
              Detail Pengajuan Lembur
            </div>
            <div class="text-xs text-[#828282] flex items-center gap-[.5rem]">
              <span>Diajukan pada:</span>
              <span class="flex items-center gap-[.25rem]">
                <span class="k-calendar text-[12px]" /> {{ formattedSubmittedDate }}
              </span>
              <span class="flex items-center gap-[.25rem]">
                <span class="k-clock-1 text-[12px]" /> {{ formattedSubmittedTime }}
              </span>
            </div>
          </div>

          <div class="border-t border-[#F1F5F9] pt-[.75rem]">
            <div class="text-xs font-semibold text-black mb-[.25rem]">
              Tanggal Lembur
            </div>
            <div class="text-sm text-[#0F172A]">
              {{ formattedOvertimeDate }}
            </div>
          </div>

          <div>
            <div class="text-xs font-semibold text-black mb-[.5rem]">
              Waktu Lembur
            </div>
            <div class="grid grid-cols-3 items-center text-center bg-[#F8FAFC] p-[.75rem] rounded-xl">
              <div>
                <div class="text-[11px] text-[#828282] mb-[.125rem]">
                  Mulai
                </div>
                <div class="text-sm font-semibold text-black">
                  {{ formattedStartTime }}
                </div>
              </div>
              <div>
                <div class="text-xs text-[#828282] mb-[.125rem]">
                  →
                </div>
                <div class="text-xs font-semibold text-[#EF4444]">
                  {{ formattedDuration }}
                </div>
              </div>
              <div>
                <div class="text-[11px] text-[#828282] mb-[.125rem]">
                  Selesai
                </div>
                <div class="text-sm font-semibold text-black">
                  {{ formattedEndTime }}
                </div>
              </div>
            </div>
          </div>

          <div>
            <div class="text-xs font-semibold text-black mb-[.25rem]">
              Tujuan Lembur
            </div>
            <div class="text-sm text-[#333333]">
              {{ detail.reason || detail.tujuan || '-' }}
            </div>
          </div>

          <div>
            <div class="text-xs font-semibold text-black mb-[.25rem]">
              Detail Lembur
            </div>
            <div class="text-sm text-[#333333]">
              {{ detail.detail || detail.detail_lembur || '-' }}
            </div>
          </div>

          <div>
            <div class="text-xs font-semibold text-black mb-[.5rem]">
              Dokumentasi
            </div>
            <div
              v-if="detailAttachments.length > 0"
              class="flex items-center gap-[.5rem] flex-wrap"
            >
              <a
                v-for="(doc, idx) in detailAttachments"
                :key="idx"
                :href="doc"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  :src="doc"
                  :alt="`Dokumentasi ${idx + 1}`"
                  class="size-20 rounded-xl object-cover border"
                >
              </a>
            </div>
            <div
              v-else
              class="text-xs text-[#828282]"
            >
              Tidak ada lampiran dokumentasi
            </div>
          </div>

          <div
            v-if="isRejected"
            class="bg-[#F8FAFC] rounded-xl p-[.75rem] border border-[#E2E8F0]"
          >
            <div class="text-[10px] font-semibold text-[#94A3B8] tracking-wider mb-[.25rem] uppercase">
              Alasan Ditolak
            </div>
            <div class="text-xs text-[#333333]">
              {{ rejectionReason }}
            </div>
          </div>
        </div>
      </div>
    </BOverlay>
  </b-sidebar>
</template>

<style scoped>
::v-deep .b-sidebar-header,
::v-deep .b-sidebar-body {
  background-color: white;
}

.dashed-divider {
  height: 2px;
  width: 100%;
  background-image: repeating-linear-gradient(
    to right,
    #E2E2E2 0,
    #E2E2E2 6px,
    transparent 6px,
    transparent 12px
  );
}
</style>

<script>
import moment from 'moment'

export default {
  name: 'DetailDrawer',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    detail: {
      type: Object,
      default: () => ({}),
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    profilePhoto() {
      return (
        this.detail.photo_profile_url
        || this.detail.user_photo
        || 'https://storage.googleapis.com/komerce/assets/avatars/image-null.png'
      )
    },
    employeeName() {
      return this.detail.name || this.detail.user_name || '-'
    },
    employeeRole() {
      return this.detail.position_name || this.detail.role || '-'
    },
    employeeDivision() {
      return this.detail.division_name || this.detail.division || ''
    },
    statusLabel() {
      if (this.detail.status_label) return this.detail.status_label
      if (this.detail.current_step_label) return this.detail.current_step_label
      const status = (this.detail.approval_status || this.detail.status || '').toString().toLowerCase()
      if (status === 'approved' || status === 'disetujui' || status === '1') return 'Disetujui'
      if (status === 'rejected' || status === 'ditolak' || status === '2') return 'Ditolak'
      if (status === 'approval_1') return 'Approver 1'
      if (status === 'approval_final') return 'Approver Final'
      return 'Menunggu'
    },
    statusBadgeVariant() {
      const status = (this.detail.approval_status || this.detail.status_label || this.detail.status || '').toString().toLowerCase()
      if (status === 'approved' || status === 'disetujui' || status === '1') return 'light-success'
      if (status === 'rejected' || status === 'ditolak' || status === '2') return 'light-danger'
      return 'light-warning'
    },
    isRejected() {
      const status = (this.detail.approval_status || this.detail.status_label || this.detail.status || '').toString().toLowerCase()
      if (status === 'rejected' || status === 'ditolak' || status === '2') return true
      if (Array.isArray(this.detail.approvals)) {
        return this.detail.approvals.some(
          a => (a.status_label && a.status_label.toLowerCase() === 'rejected')
            || a.status === 'rejected'
            || a.status === 2,
        )
      }
      return false
    },
    rejectionReason() {
      if (this.detail.rejection_reason) return this.detail.rejection_reason
      if (Array.isArray(this.detail.approvals)) {
        const rejected = this.detail.approvals.find(
          a => ((a.status_label && a.status_label.toLowerCase() === 'rejected')
            || a.status === 'rejected'
            || a.status === 2) && a.notes,
        )
        if (rejected && rejected.notes) return rejected.notes
      }
      return this.detail.notes || 'Tidak ada keterangan penolakan.'
    },
    formattedStartTime() {
      return this.formatTime(this.detail.start_time)
    },
    formattedEndTime() {
      return this.formatTime(this.detail.end_time)
    },
    formattedDuration() {
      return this.formatDuration(
        this.detail.duration,
        this.detail.duration_minutes,
        this.detail.start_time,
        this.detail.end_time,
      )
    },
    formattedSubmittedDate() {
      const raw = this.detail.submitted_at || this.detail.created_at
      if (!raw) return '-'
      try {
        const d = moment(raw)
        if (d.isValid()) {
          d.locale('id')
          return d.format('dddd, DD MMMM YYYY')
        }
      } catch (e) {
        // fallback
      }
      return raw
    },
    formattedSubmittedTime() {
      const raw = this.detail.submitted_at || this.detail.created_at
      if (!raw) return '-'
      try {
        const d = moment(raw)
        if (d.isValid()) {
          return d.format('HH:mm')
        }
      } catch (e) {
        // fallback
      }
      return '-'
    },
    formattedOvertimeDate() {
      const raw = this.detail.overtime_date || this.detail.date || this.detail.created_at
      if (!raw) return '-'
      try {
        const d = moment(raw)
        if (d.isValid()) {
          d.locale('id')
          return d.format('dddd, DD MMMM YYYY')
        }
      } catch (e) {
        // fallback
      }
      return raw
    },
    detailAttachments() {
      const list = this.detail.images || this.detail.attachments || this.detail.files || this.detail.documentation || []
      if (Array.isArray(list)) {
        return list.map(item => (typeof item === 'string' ? item : item.url || item.path || '')).filter(Boolean)
      }
      return []
    },
  },
  methods: {
    onClose() {
      this.$emit('on-close')
    },
    formatTime(val) {
      if (!val) return '-'
      const str = String(val).trim()
      const timeMatch = str.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/)
      if (timeMatch) {
        return `${timeMatch[1].padStart(2, '0')}:${timeMatch[2]}`
      }
      try {
        const d = moment(str)
        if (d.isValid()) {
          return d.format('HH:mm')
        }
      } catch (e) {
        // fallback
      }
      return str
    },
    formatDuration(dur, minutes, startTime, endTime) {
      if (typeof minutes === 'number' && !Number.isNaN(minutes) && minutes > 0) {
        const h = Math.floor(minutes / 60)
        const m = minutes % 60
        return `${h}j ${m}m`
      }

      if (typeof dur === 'number' && !Number.isNaN(dur) && dur > 0) {
        const h = Math.floor(dur / 60)
        const m = dur % 60
        return `${h}j ${m}m`
      }

      if (typeof dur === 'string' && dur.trim()) {
        const s = dur.trim()
        if (/^\d+j\s*\d+m$/i.test(s)) {
          return s.toLowerCase()
        }
        const timeMatch = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/)
        if (timeMatch) {
          const h = parseInt(timeMatch[1], 10)
          const m = parseInt(timeMatch[2], 10)
          return `${h}j ${m}m`
        }
        let hours = 0
        let mins = 0
        let matched = false

        const jamMatch = s.match(/(\d+(?:\.\d+)?)\s*(?:jam|j\b)/i)
        if (jamMatch) {
          const val = parseFloat(jamMatch[1])
          hours = Math.floor(val)
          mins = Math.round((val - hours) * 60)
          matched = true
        }
        const menitMatch = s.match(/(\d+)\s*(?:menit|m\b)/i)
        if (menitMatch) {
          mins += parseInt(menitMatch[1], 10)
          matched = true
        }
        if (matched) {
          hours += Math.floor(mins / 60)
          mins %= 60
          return `${hours}j ${mins}m`
        }
        if (/^\d+$/.test(s)) {
          const num = parseInt(s, 10)
          if (num >= 60) {
            return `${Math.floor(num / 60)}j ${num % 60}m`
          }
          return `${num}j 0m`
        }
      }

      if (startTime && endTime) {
        const sMatch = String(startTime).match(/(\d{1,2}):(\d{2})/)
        const eMatch = String(endTime).match(/(\d{1,2}):(\d{2})/)
        if (sMatch && eMatch) {
          const startM = parseInt(sMatch[1], 10) * 60 + parseInt(sMatch[2], 10)
          let endM = parseInt(eMatch[1], 10) * 60 + parseInt(eMatch[2], 10)
          if (endM < startM) endM += 24 * 60
          const diff = endM - startM
          return `${Math.floor(diff / 60)}j ${diff % 60}m`
        }
      }

      return dur ? String(dur) : '-'
    },
  },
}
</script>
